<script setup>
/**
 * CatalogModeChooser
 * Full-screen choice shown when the client opens "קטלוג" from the side menu:
 *   - קטלוג רגיל     -> the normal catalog (unchanged)
 *   - קטלוג אימרסיבי -> the immersive, full-screen, swipeable catalog
 * Pure presentational: the host decides where each choice navigates. Selections
 * emit { remember } from the "אל תראה שוב" checkbox — persisting it is the
 * host's job.
 */
import { ref, toRef, watch, onMounted, onUnmounted } from 'vue'
import { useBackButtonClose } from '../../../core/composables/useBackButtonClose.js'

const props = defineProps({
  open: { type: Boolean, default: false },
})

const emit = defineEmits(['select-normal', 'select-immersive', 'close'])

// "אל תראה שוב" — checked state resets every time the chooser opens so a stale
// tick from a previous visit never silently locks the user into a mode.
const dontShowAgain = ref(false)
watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) dontShowAgain.value = false
  }
)

function close() {
  emit('close')
}

// Back button closes the chooser instead of navigating away.
const { neutralizeHistory } = useBackButtonClose(toRef(props, 'open'), close)

// Escape closes the chooser (mirrors the other immersive overlays).
function onKeydown(e) {
  if (props.open && e.key === 'Escape') close()
}
onMounted(() => window.addEventListener('keydown', onKeydown))
onUnmounted(() => window.removeEventListener('keydown', onKeydown))

// On a selection the host navigates (router.push) while the chooser closes. We
// must neutralize the pushed history entry FIRST so the close watcher does not
// fire history.back() and race/undo the navigation (mirrors CartDrawer).
function selectNormal() {
  neutralizeHistory()
  emit('select-normal', { remember: dontShowAgain.value })
}

function selectImmersive() {
  neutralizeHistory()
  emit('select-immersive', { remember: dontShowAgain.value })
}
</script>

<template>
  <Teleport to="body">
    <Transition name="chooser-fade">
      <div v-if="open" class="chooser-overlay" dir="rtl" @click.self="close">
        <button class="chooser-close" type="button" aria-label="סגירה" @click="close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
        <div class="chooser-modal" role="dialog" aria-modal="true" aria-label="בחירת מצב קטלוג">
          <h2 class="chooser-title">איך תרצו לעיין בקטלוג?</h2>
          <p class="chooser-subtitle">בחרו את חוויית הגלישה המתאימה לכם</p>

          <div class="chooser-options">
            <!-- Normal -->
            <button class="chooser-card" type="button" @click="selectNormal">
              <span class="chooser-card-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="3" width="7" height="7" rx="1" />
                  <rect x="14" y="3" width="7" height="7" rx="1" />
                  <rect x="3" y="14" width="7" height="7" rx="1" />
                  <rect x="14" y="14" width="7" height="7" rx="1" />
                </svg>
              </span>
              <span class="chooser-card-title">קטלוג רגיל</span>
              <span class="chooser-card-desc">תצוגת רשת מלאה עם מחלקות, חיפוש וסינון</span>
            </button>

            <!-- Immersive -->
            <button
              class="chooser-card chooser-card-featured"
              type="button"
              @click="selectImmersive"
            >
              <span class="chooser-card-badge">חדש</span>
              <span class="chooser-card-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="7" y="3" width="10" height="18" rx="2" />
                  <path d="M11 18h2" />
                </svg>
              </span>
              <span class="chooser-card-title">קטלוג אימרסיבי</span>
              <span class="chooser-card-desc">חוויה מסך מלא — פריט אחר פריט, החליקו לעיון</span>
            </button>
          </div>

          <label class="chooser-remember">
            <input v-model="dontShowAgain" type="checkbox" />
            <span>אל תראה שוב</span>
          </label>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* Full-screen takeover, centered content, identical on mobile and desktop.
   Block scroll container (not flex) so tall content on short viewports scrolls
   instead of being clipped above/below the fold. */
.chooser-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  overflow-y: auto;
  background: var(--bg-primary);
}

.chooser-modal {
  position: relative;
  min-height: 100%;
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  text-align: center;
  padding: calc(var(--spacing-xl) + env(safe-area-inset-top, 0px)) var(--spacing-lg)
    calc(var(--spacing-xl) + env(safe-area-inset-bottom, 0px));
}

.chooser-close {
  position: fixed;
  top: calc(var(--spacing-md) + env(safe-area-inset-top, 0px));
  inset-inline-start: var(--spacing-md);
  z-index: 1;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: var(--border-radius-sm);
  background: var(--bg-secondary);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.chooser-close:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.chooser-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-xs);
}

.chooser-subtitle {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  margin: 0 0 var(--spacing-lg);
}

.chooser-options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-md);
  width: 100%;
  margin-top: var(--spacing-md);
}

.chooser-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-lg) var(--spacing-md);
  background: var(--bg-secondary);
  border: 2px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  cursor: pointer;
  font-family: var(--font-family);
  transition: all var(--transition-fast);
}

.chooser-card:hover {
  border-color: var(--color-primary);
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.chooser-card:active {
  transform: translateY(0) scale(0.98);
}

.chooser-card-featured {
  background: var(--gradient-primary);
  border-color: transparent;
}

.chooser-card-featured:hover {
  box-shadow: 0 12px 24px -6px rgba(30, 41, 59, 0.45);
}

.chooser-card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: var(--border-radius-lg);
  background: var(--bg-primary);
  color: var(--color-primary);
}

.chooser-card-featured .chooser-card-icon {
  background: rgba(255, 255, 255, 0.15);
  color: #ffffff;
}

.chooser-card-icon svg {
  width: 28px;
  height: 28px;
}

.chooser-card-title {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
}

.chooser-card-featured .chooser-card-title {
  color: #ffffff;
}

.chooser-card-desc {
  font-size: var(--font-size-xs);
  line-height: 1.4;
  color: var(--text-muted);
}

.chooser-card-featured .chooser-card-desc {
  color: rgba(255, 255, 255, 0.85);
}

.chooser-card-badge {
  position: absolute;
  top: var(--spacing-sm);
  inset-inline-end: var(--spacing-sm);
  padding: 2px 8px;
  border-radius: var(--border-radius-full);
  background: var(--color-success);
  color: #ffffff;
  font-size: 10px;
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.3px;
}

.chooser-remember {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  margin-top: var(--spacing-lg);
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  cursor: pointer;
  user-select: none;
  transition: color var(--transition-fast);
}

.chooser-remember:hover {
  color: var(--text-secondary);
}

.chooser-remember input {
  width: 16px;
  height: 16px;
  margin: 0;
  accent-color: var(--color-primary);
  cursor: pointer;
}

.chooser-fade-enter-active,
.chooser-fade-leave-active {
  transition: opacity 0.25s ease;
}

.chooser-fade-enter-from,
.chooser-fade-leave-to {
  opacity: 0;
}

@media (max-width: 520px) {
  .chooser-options {
    grid-template-columns: 1fr;
  }
}

@media (prefers-reduced-motion: reduce) {
  .chooser-card,
  .chooser-fade-enter-active,
  .chooser-fade-leave-active {
    transition: none;
  }
}
</style>
